import { useEffect, useState } from "react";

import { useAppSetting } from "@swa-ui/application";
import { logger } from "@swa-ui/log";

const FETCH_TIMEOUT_IN_MILLIS = 5000;

export const useVacationDeals = ({ packageIds }) => {
  const [orderedResults, setOrderedResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const timeoutInMillis = useAppSetting("swavOjtApiTimeoutInMillis", FETCH_TIMEOUT_IN_MILLIS);
  const dealsMap = generateDealsMap();

  useEffect(() => {
    const initiateFetch = async () => {
      await fetchAndSetAllDeals();
    };

    initiateFetch();
  }, []);

  async function fetchAndSetAllDeals() {
    await Promise.allSettled(
      packageIds.map(({ tripProfileId, searchProfileId }) =>
        fetchAndSetOneDeal(tripProfileId, searchProfileId)
      )
    );

    setOrderedResults(getOrderedArrayFromDealsMap());
    setLoading(false);
  }

  async function fetchAndSetOneDeal(tripProfileId, searchProfileId) {
    const dealMapId = getUniqueDealId(tripProfileId, searchProfileId);
    const dealMapObject = dealsMap.get(dealMapId);

    // Fetch first OJT service - LandingPageRetrieval API
    const searchProfileResponse = await fetchResultSetsBySearchProfile(
      tripProfileId,
      searchProfileId
    );
    const { data: searchProfileData, error: searchProfileError } = searchProfileResponse || {};
    const { deepLink, resultSetId } = searchProfileData?.searches?.minResultSet || {};

    if (searchProfileError) {
      dealMapObject.error = searchProfileError;
      logger.error(dealMapObject.error);
    } else if (!deepLink || !resultSetId) {
      dealMapObject.error = `Missing min resultSetId or deepLink for vacation deal ${tripProfileId} ${searchProfileId}`;
      logger.error(dealMapObject.error);
    } else {
      // Fetch second OJT service - tRetail API
      const resultSetResponse = await fetchResultSetByResultSetID(resultSetId);
      const { data: resultSetData, error: resultSetError } = resultSetResponse || {};

      if (resultSetError) {
        dealMapObject.error = resultSetError;
        logger.error(dealMapObject.error);
      } else if (!isValidResultSetData(resultSetData)) {
        logger.error(
          `One or more required data elements for vacation deal is missing for ${tripProfileId} ${searchProfileId}`
        );
      } else {
        dealMapObject.data = {
          navigationLink: deepLink,
          packageData: resultSetData?.packageOptions?.[0],
        };
      }
    }

    dealsMap.set(dealMapId, dealMapObject);
  }

  async function fetchResultSetsBySearchProfile(tripProfileId, searchProfileId) {
    return await fetchWithTimeout(
      `/vacations/packages/tripProfiles/${tripProfileId}/searchProfiles/${searchProfileId}`
    );
  }

  async function fetchResultSetByResultSetID(resultSetId) {
    return await fetchWithTimeout(`/vacations/packages/${resultSetId}`);
  }

  async function fetchWithTimeout(endpoint) {
    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort({ message: `Request timed out ${endpoint}` }),
      timeoutInMillis
    );
    let response;
    let data;
    let errorResponse;

    try {
      response = await fetch(endpoint, {
        signal: controller.signal,
      });

      if (response?.ok) {
        data = await response.json();
      } else {
        errorResponse = await response?.text();
      }
    } catch (error) {
      if (response?.status === 404 && !errorResponse) {
        errorResponse = `404 error ${endpoint}`;
      } else {
        errorResponse = JSON.stringify(error);
      }
    } finally {
      clearTimeout(timer);
    }

    return { data, error: errorResponse };
  }

  function generateDealsMap() {
    const resultMap = new Map();

    packageIds.forEach(({ tripProfileId, searchProfileId }) => {
      const dealData = {
        data: undefined,
        error: undefined,
      };

      resultMap.set(getUniqueDealId(tripProfileId, searchProfileId), dealData);
    });

    return resultMap;
  }

  function getUniqueDealId(id1, id2) {
    return `${id1}_${id2}`;
  }

  function isValidResultSetData(resultSetData) {
    const { packageOptions } = resultSetData || {};

    const { flightOptions, hotelOption, prices } = packageOptions?.[0] || {};
    const { iataCode, dateTime, city } = flightOptions?.[0]?.departure || {};
    const { address, duration, images, name, starRating } = hotelOption || {};
    const earnQuantity = prices?.[0]?.earn?.total?.earnQuantity;
    const totalSalesAmountPerGuest = prices?.[0]?.metadata?.label?.find(
      ({ code }) => code === "PACKAGE_COST_PER_PAX"
    );
    let isValid = true;

    if (
      !resultSetData ||
      !packageOptions ||
      !flightOptions ||
      !iataCode ||
      !dateTime ||
      !city ||
      !address ||
      (address?.country?.code === "US" && !address?.state) ||
      (address?.country?.code !== "US" && !address?.country?.name) ||
      !duration ||
      !images ||
      !name ||
      !starRating ||
      !earnQuantity ||
      !totalSalesAmountPerGuest
    ) {
      isValid = false;
    }

    return isValid;
  }

  function getOrderedArrayFromDealsMap() {
    const orderedArray = [];

    dealsMap.forEach((dealMap) => {
      const { data, error } = dealMap || {};

      if (!error && data) {
        orderedArray.push(data);
      }
    });

    return orderedArray;
  }

  return { loading, orderedResults };
};
